/**
 * The scrapbook: every photo two people have sent each other with their
 * letters, pinned up as Polaroids on a page per month. Tap a print to see it
 * full size; from there, "Read the letter" opens the letter it came with, and
 * the sender can take their photo back.
 *
 * Reached from the conversation's menu and its details. Sending photos needs
 * trust; keeping them does not — a photo that has arrived stays here unless
 * its sender takes it back.
 */

import { useMemo, useState } from 'react';
import { ArrowLeft, Images } from 'lucide-react';
import { TIER_LABELS } from '@toiyp/shared';
import { useGetSessionQuery } from '../auth/api';
import { useThreadQuery } from '../letters/api';
import { useMineQuery } from '../relationships/api';
import { Polaroid, PhotoViewer } from './Polaroid';
import { photoCount, printCaption, scrapbookPages, scrapbookPhotos } from './scrapbook';

/** The pair's photos, and whether they can send new ones just now. */
export function useScrapbook(relationshipId: string) {
  const { data: session } = useGetSessionQuery();
  const { data: thread, isLoading } = useThreadQuery({ relationshipId });
  const { data: rels } = useMineQuery();
  const photos = useMemo(() => scrapbookPhotos(thread ?? [], session?.id), [thread, session?.id]);
  const rel = rels?.find((r) => r.id === relationshipId);
  const canSend = rel?.capabilities.includes('photos') ?? false;
  return { photos, canSend, loading: isLoading || !rels };
}

/**
 * "Scrapbook · 4 photos", with the newest three fanned out. Shown beside a
 * conversation once there is something in it.
 */
export function ScrapbookCard({ relationshipId, onOpen }: { relationshipId: string; onOpen: () => void }) {
  const { photos } = useScrapbook(relationshipId);
  if (photos.length === 0) return null;
  return (
    <button type="button" className="scrapbook-card" onClick={onOpen}>
      <span className="scrapbook-card__fan" aria-hidden="true">
        {photos.slice(0, 3).map((p, i) => (
          <Polaroid key={p.photo.id} photo={p.photo} size="small" tilt={[-8, 4, -2][i]} />
        ))}
      </span>
      <span className="scrapbook-card__text">
        <span className="scrapbook-card__title">Scrapbook</span>
        <span className="scrapbook-card__meta">{photoCount(photos.length)} from your letters</span>
      </span>
    </button>
  );
}

export function ScrapbookScreen({
  relationshipId,
  peerName,
  onBack,
  onOpenLetter,
}: {
  relationshipId: string;
  peerName: string;
  onBack: () => void;
  onOpenLetter: (letterId: string) => void;
}) {
  const { photos, canSend, loading } = useScrapbook(relationshipId);
  const pages = useMemo(() => scrapbookPages(photos), [photos]);
  const [open, setOpen] = useState<number | null>(null);
  const prints = useMemo(
    () =>
      photos.map((p) => ({
        photo: p.photo,
        caption: printCaption(p, peerName),
        action: { label: 'Read the letter', onClick: () => onOpenLetter(p.letterId) },
        canTakeBack: p.mine,
      })),
    [photos, peerName, onOpenLetter],
  );
  const indexOf = (id: string) => photos.findIndex((p) => p.photo.id === id);

  return (
    <section className="screen" aria-label={`Scrapbook with @${peerName}`}>
      <header className="screen__header">
        <button type="button" className="screen__back" onClick={onBack} aria-label="Back">
          <ArrowLeft size={20} />
        </button>
        <span className="screen__title">Scrapbook</span>
      </header>

      <div className="scrapbook">
        {loading ? (
          <p className="scrapbook__lead">Opening the scrapbook…</p>
        ) : (
          <>
            <p className="scrapbook__lead">
              {photos.length > 0
                ? `${photoCount(photos.length)} you and @${peerName} have sent each other by post.`
                : `Photos you and @${peerName} send with your letters are pinned up here.`}
            </p>
            {!canSend && (
              <p className="scrapbook__note">
                <Images size={14} aria-hidden="true" />
                {photos.length > 0
                  ? `These are yours to keep. Sending new ones opens again once you’re ${TIER_LABELS.trusted}.`
                  : `Sending photos opens once you’re ${TIER_LABELS.trusted}.`}
              </p>
            )}
            {photos.length === 0 && canSend && (
              <p className="scrapbook__note">
                <Images size={14} aria-hidden="true" />
                Attach one to a letter while it’s still in the post, and it lands here when it arrives.
              </p>
            )}

            {pages.map((page) => (
              <section key={page.key} className="scrapbook__page" aria-label={page.label}>
                <h3 className="scrapbook__month">{page.label}</h3>
                <div className="scrapbook__prints">
                  {page.photos.map((p) => (
                    <Polaroid
                      key={p.photo.id}
                      photo={p.photo}
                      caption={printCaption(p, peerName)}
                      tape
                      onOpen={() => setOpen(indexOf(p.photo.id))}
                    />
                  ))}
                </div>
              </section>
            ))}
          </>
        )}
      </div>

      {open != null && prints[open] && (
        <PhotoViewer prints={prints} peerName={peerName} index={open} onIndex={setOpen} onClose={() => setOpen(null)} />
      )}
    </section>
  );
}
